import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest } from '../utils/api';
import { Character, EquipmentSlots, InventoryItem } from './equipmentTypes';
import { getSlotEnumValue, canEquipInSlot } from './equipmentUtils';
import EquipmentSlotGrid from './EquipmentSlotGrid';
import EquipmentItemList from './EquipmentItemList';
import { useConfirm } from './ConfirmModal';
import './CharacterEquipment.css';

interface CharacterEquipmentProps {
  characterId: number;
  onBack: () => void;
}

const CharacterEquipment: React.FC<CharacterEquipmentProps> = ({ characterId, onBack }) => {
  const { handleAuthError } = useAuth();
  const { checkAndStartTutorial } = useTutorial();
  const { confirm, confirmDialog } = useConfirm();

  const [character, setCharacter] = useState<Character | null>(null);
  const [equipment, setEquipment] = useState<EquipmentSlots | null>(null);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [consumables, setConsumables] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [draggedItem, setDraggedItem] = useState<InventoryItem | null>(null);
  const [slotFilter, setSlotFilter] = useState<string>('all');

  useEffect(() => {
    const loadData = async () => {
      const char = await loadCharacterData();
      await loadEquipment();
      await loadInventory(char);
    };
    loadData();
  }, [characterId]);

  useEffect(() => {
    if (equipment && !isLoading) {
      checkAndStartTutorial('equipment-basics');
    }
  }, [equipment, isLoading, checkAndStartTutorial]);

  const loadCharacterData = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/characters/${characterId}`, { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        setCharacter(data);
        return data;
      }
    } catch (err) {
      logger.error('Error loading character:', err);
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    }
    return null;
  };

  const loadEquipment = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/equipment/v2/character/${characterId}`, { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        setEquipment(data);
      }
    } catch (err) {
      logger.error('Error loading equipment:', err);
      setError('Failed to load equipment');
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    } finally {
      setIsLoading(false);
    }
  };

  const loadInventory = async (char: Character | null) => {
    try {
      const response = await makeAuthenticatedRequest(`/crafting/crafted-items`, { method: 'GET' });
      if (!response.ok) return;

      const data = await response.json();

      if (!char) {
        logger.error('❌ Cannot filter inventory: character data not loaded');
        setInventory([]);
        return;
      }

      const unequippedItems = data.filter((item: InventoryItem) => {
        if (item.isEquipped) return false;
        if (item.requiredLevel > char.level) return false;
        if (item.requiredClass && item.requiredClass.trim() !== '') {
          if (item.requiredClass.toLowerCase() !== char.class.toLowerCase()) return false;
        }
        return true;
      });

      const consumableItems = unequippedItems.filter((item: InventoryItem) => item.itemType === 'Consumable');
      const equipmentItems = unequippedItems.filter((item: InventoryItem) => item.itemType !== 'Consumable');

      setInventory(equipmentItems.sort((a: InventoryItem, b: InventoryItem) => a.itemName.localeCompare(b.itemName)));
      setConsumables(consumableItems.sort((a: InventoryItem, b: InventoryItem) => a.itemName.localeCompare(b.itemName)));
    } catch (err) {
      logger.error('Error loading inventory:', err);
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    }
  };

  const equipItem = async (itemId: number, slot: string) => {
    try {
      const response = await makeAuthenticatedRequest(`/equipment/v2/equip`, {
        method: 'POST',
        body: JSON.stringify({ characterId, itemId, equipmentSlot: getSlotEnumValue(slot) }),
      });
      if (response.ok) {
        await loadEquipment();
        await loadInventory(character);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to equip item');
      }
    } catch (err) {
      logger.error('Error equipping item:', err);
      setError('Failed to equip item');
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    }
  };

  const unequipItem = async (slot: string) => {
    try {
      const response = await makeAuthenticatedRequest(`/equipment/v2/unequip`, {
        method: 'POST',
        body: JSON.stringify({ characterId, equipmentSlot: getSlotEnumValue(slot) }),
      });
      if (response.ok) {
        await loadEquipment();
        await loadInventory(character);
      }
    } catch (err) {
      logger.error('Error unequipping item:', err);
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    }
  };

  const unequipAll = async () => {
    if (!(await confirm({
      title: 'Unequip All',
      message: 'Unequip all items from this character?',
      confirmText: 'Unequip All',
    }))) return;
    setError('');
    const newSlots = ['mainHand', 'offHand', 'head', 'chest', 'legs', 'feet', 'hands', 'necklace', 'ring1', 'ring2', 'belt', 'back'];
    const legacySlots = ['weapon', 'armor', 'accessory'];
    try {
      await Promise.all([
        ...newSlots.map(slot =>
          makeAuthenticatedRequest(`/equipment/v2/unequip`, { method: 'POST', body: JSON.stringify({ characterId, equipmentSlot: getSlotEnumValue(slot) }) })
            .catch(err => logger.debug(`Slot ${slot} empty or error:`, err))
        ),
        ...legacySlots.map(slotType =>
          makeAuthenticatedRequest(`/equipment/unequip`, { method: 'POST', body: JSON.stringify({ characterId, slotType }) })
            .catch(err => logger.error(`Legacy slot ${slotType}:`, err))
        ),
      ]);
      await loadEquipment();
      await loadInventory(character);
    } catch (err) {
      logger.error('Error unequipping all items:', err);
      setError('Failed to unequip all items');
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    }
  };

  const consumeItem = async (itemId: number) => {
    try {
      setError('');
      setMessage('');
      const response = await makeAuthenticatedRequest(`/crafting/use-consumable`, {
        method: 'POST',
        body: JSON.stringify({ itemId, characterId }),
      });
      if (response.ok) {
        const data = await response.json();
        setMessage(data.message);
        await loadEquipment();
        await loadInventory(character);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to use item');
      }
    } catch (err) {
      logger.error('Error using consumable:', err);
      setError('Failed to use consumable');
      if (err instanceof Error && err.message.includes('401')) handleAuthError();
    }
  };

  const handleDragStart = (item: InventoryItem) => setDraggedItem(item);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleDrop = (e: React.DragEvent, slot: string) => {
    e.preventDefault();
    if (!draggedItem) return;
    if (!canEquipInSlot(draggedItem, slot)) {
      const slotName = draggedItem.equipmentSlot !== undefined ? `slot ${draggedItem.equipmentSlot}` : 'unknown slot';
      setError(`Cannot equip ${draggedItem.itemName} (${slotName}) in ${slot} slot`);
      setDraggedItem(null);
      return;
    }
    equipItem(draggedItem.id, slot);
    setDraggedItem(null);
  };

  if (isLoading) return <div className="equipment-loading">Loading equipment...</div>;
  if (!equipment || !character) return <div className="equipment-error">Failed to load character equipment</div>;

  return (
    <div className="character-equipment-screen">
      {confirmDialog}
      <div className="equipment-header">
        <button onClick={onBack} className="back-button">← Back to Characters</button>
        <h2>{character.name}'s Equipment</h2>
        <button onClick={unequipAll} className="unequip-all-button">Unequip All</button>
      </div>

      {error && <div className="error-message" style={{ color: '#f44336', padding: '10px', marginBottom: '10px', background: 'rgba(244, 67, 54, 0.1)', borderRadius: '4px' }}>{error}</div>}
      {message && <div className="success-message" style={{ color: '#4caf50', padding: '10px', marginBottom: '10px', background: 'rgba(76, 175, 80, 0.1)', borderRadius: '4px' }}>{message}</div>}

      <div className="equipment-container">
        <EquipmentSlotGrid
          equipment={equipment}
          onUnequip={unequipItem}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
        <EquipmentItemList
          inventory={inventory}
          consumables={consumables}
          slotFilter={slotFilter}
          onSlotFilterChange={setSlotFilter}
          onDragStart={handleDragStart}
          onConsumeItem={consumeItem}
        />
      </div>
    </div>
  );
};

export default CharacterEquipment;
